import { NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  LayoutDashboard, Wrench, Users, Factory, Package, Truck, Warehouse,
  Settings, LogOut, Menu, X, Boxes, ClipboardList, UserRound, ScrollText, QrCode
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import AmaLogo from "./AmaLogo";
import { Button } from "@/components/ui/button";
import QRScanner from "./QRScanner";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/accettazione", label: "Nuova accettazione", icon: ClipboardList, testid: "nav-new-intake", primary: true, roles: ["reception"] },
  { to: "/pratiche", label: "Pratiche", icon: Wrench, testid: "nav-repairs" },
  { to: "/prodotti-in-riparazione", label: "Prodotti", icon: Boxes, testid: "nav-items" },
  { to: "/spedizioni", label: "Spedizioni", icon: Truck, testid: "nav-shipments" },
  { to: "/magazzino", label: "Magazzino", icon: Warehouse, testid: "nav-warehouse" },
  { to: "/clienti", label: "Clienti", icon: Users, testid: "nav-customers" },
  { to: "/produttori", label: "Produttori", icon: Factory, testid: "nav-manufacturers" },
  { to: "/catalogo", label: "Catalogo prodotti", icon: Package, testid: "nav-products" },
  { to: "/impostazioni", label: "Impostazioni", icon: Settings, testid: "nav-settings", roles: ["administrator"] },
  { to: "/impostazioni/registro-attivita", label: "Registro attività", icon: ScrollText, testid: "nav-audit", roles: ["administrator"] },
  { to: "/profilo", label: "Il mio profilo", icon: UserRound, testid: "nav-profile" },
];

export default function Layout({ children }) {
  const { user, logout, hasRole } = useAuth();
  const [open, setOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const navigate = useNavigate();

  const filtered = NAV.filter((n) => !n.roles || hasRole(...n.roles));

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-slate-50">
      {/* Mobile top bar */}
      <div className="md:hidden sticky top-0 z-40 bg-white border-b border-slate-200 flex items-center justify-between px-3 py-2.5 shadow-sm">
        <button data-testid="mobile-menu-btn" onClick={() => setOpen(true)} className="p-2 -ml-2">
          <Menu className="w-6 h-6 text-[#0a0a2e]" />
        </button>
        <AmaLogo size={28} />
        <div className="flex items-center gap-1">
          <button
            data-testid="mobile-qr-scan"
            onClick={() => setScanOpen(true)}
            className="p-2 rounded-full text-[#0a0a2e] hover:bg-fuchsia-50"
            aria-label="Scansiona QR pratica"
            title="Scansiona QR pratica"
          >
            <QrCode className="w-5 h-5" />
          </button>
          <button
            data-testid="mobile-new-intake"
            onClick={() => navigate("/accettazione")}
            className="ama-btn-primary text-xs !py-1.5 !px-3"
          >
            + Accettazione
          </button>
        </div>
      </div>

      {/* Sidebar */}
      <aside
        data-testid="app-sidebar"
        className={`${open ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 transition-transform duration-200 fixed md:static inset-y-0 left-0 z-50 w-72 bg-white border-r border-slate-200 flex flex-col`}
      >
        <div className="flex items-center justify-between px-5 py-5 border-b border-slate-100">
          <AmaLogo size={38} />
          <button className="md:hidden" onClick={() => setOpen(false)} aria-label="Chiudi menu">
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
          {filtered.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={n.testid}
              onClick={() => setOpen(false)}
              className={({ isActive }) => {
                if (n.primary) {
                  return `flex items-center gap-3 mx-2 my-1 rounded-xl px-3 py-2.5 text-sm font-semibold text-white shadow-md ${isActive ? "" : ""}`;
                }
                return `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-fuchsia-50 text-[#d63384] border-l-2 border-[#d63384]"
                    : "text-slate-700 hover:bg-slate-100"
                }`;
              }}
              style={n.primary ? { background: "linear-gradient(135deg,#d63384,#6f42c1)" } : undefined}
            >
              <n.icon className="w-4 h-4" />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-slate-100 p-3 space-y-2">
          <Button
            data-testid="sidebar-qr-scan"
            onClick={() => setScanOpen(true)}
            variant="outline"
            className="w-full rounded-full text-sm border-[#d63384]/40 text-[#d63384] hover:bg-fuchsia-50"
          >
            <QrCode className="w-4 h-4 mr-2" /> Scansiona QR pratica
          </Button>
          <div className="mb-2 px-2">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Utente</div>
            <div className="text-sm font-semibold text-[#0a0a2e]" data-testid="current-user-name">{user?.full_name}</div>
            <div className="text-xs text-slate-500" data-testid="current-user-email">{user?.email}</div>
          </div>
          <Button
            data-testid="logout-btn"
            onClick={logout}
            variant="outline"
            className="w-full rounded-full text-sm"
          >
            <LogOut className="w-4 h-4 mr-2" /> Esci
          </Button>
        </div>
      </aside>

      {open && <div className="md:hidden fixed inset-0 bg-black/40 z-40" onClick={() => setOpen(false)} />}

      {scanOpen && <QRScanner onClose={() => setScanOpen(false)} />}

      {/* Main */}
      <main className="flex-1 min-w-0">
        {children}
      </main>
    </div>
  );
}
